import { useEffect, useState } from "react";
import apiClient from "../../../shared/API/apiClient";
import Toast from "../../../shared/components/Toast";
import ActivityMappingModal from "./ActivityMappingModal";
import ViewActivitiesModal from "../LocationMapping/ViewLocationModal";

const ActivityMappingPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [viewRow, setViewRow] = useState(null);

  const showToast = (message, type = "success") =>
    setToast({ message, type });

  // ── Fetch ─────────────────────────────────────────────────
  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("/api/reportingyear-activity");
      setRows(res.data?.data || []);
    } catch (err) {
      console.error("Activity mapping fetch error", err);
      showToast(
        err.response?.data?.message || "Failed to load activity mappings",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // ── Edit click handler ────────────────────────────────────
  const handleEditClick = (yr) => {
    if (yr.is_locked) {
      showToast("Cannot edit — this reporting year is locked.", "error");
      return;
    }
    setEditRow(yr);
  };

  // ── After modal saves ─────────────────────────────────────
  const handleSaved = (message) => {
    fetchData();
    showToast(message || "Activities mapped successfully", "success");
    setEditRow(null);
  };

  // ── Helpers ───────────────────────────────────────────────
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  const renderMappedActivities = (yr) => {
    // If activities exist, show the "View" link
    if (yr.activity_names) {
      const namesArray = yr.activity_names
        .split(",")
        .map((n) => n.trim())
        .filter(Boolean);

      return (
        <span
          className="view-link"
          onClick={() => setViewRow(yr)}
          style={{ 
            cursor: "pointer", 
            color: "#007bff", 
            fontWeight: "600", 
            textDecoration: "underline" 
          }}
        >
          View {namesArray.length} {namesArray.length === 1 ? "Activity" : "Activities"}
        </span>
      );
    }

    // If no activities and not locked, show "Map" link
    if (!yr.is_locked) {
      return (
        <span
          className="map-link"
          onClick={() => setEditRow(yr)}
          style={{ color: "#28a745", cursor: "pointer" }}
        >
          + Map Activities
        </span>
      );
    }

    return <span className="empty-cell">—</span>;
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="dash-page">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Header */}
      <div className="le-page-header">
        <div>
          <div className="dash-page-title">Activity Mapping</div>
          <div className="dash-page-subtitle">
            Map activities to reporting years for BRSR reporting
          </div>
        </div>
      </div>

      {/* List */}
      <div className="le-card">
        {loading ? (
          <div className="le-empty">
            <div className="le-empty-text">Loading activity mappings...</div>
          </div>
        ) : rows.length === 0 ? (
          <div className="le-empty">
            <div className="le-empty-text">No reporting years found.</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="le-table">
              <thead>
                <tr>
                  <th>Reporting Year</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                  <th>Mapped Activities</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((yr) => {
                  const isLocked = Boolean(yr.is_locked);
                  return (
                    <tr key={yr.id}>
                      <td data-label="Reporting Year">{yr.year_label}</td>
                      <td data-label="Start Date">{formatDate(yr.start_date)}</td>
                      <td data-label="End Date">{formatDate(yr.end_date)}</td>
                      <td data-label="Status">
                        <span className={`badge ${isLocked ? "locked" : "unlocked"}`}>
                          {isLocked ? "Closed" : "Active"}
                        </span>
                      </td>
                      <td data-label="Mapped Activities">
                        {renderMappedActivities(yr)}
                      </td>
                      <td data-label="Actions">
                        <div className="le-actions">
                          {!isLocked && (
                            <span
                              className="action-item edit"
                              onClick={() => handleEditClick(yr)}
                              title="Edit activity mapping"
                            >
                              <img
                                src="https://cdn-icons-png.flaticon.com/512/1159/1159633.png"
                                alt="edit"
                                className="action-icon"
                              />
                              Edit
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mapping Modal */}
      {editRow && (
        <ActivityMappingModal
          data={editRow}
          onClose={() => setEditRow(null)}
          onSave={handleSaved}
        />
      )}

      {/* View-Only Modal */}
     {viewRow && (
        <ViewActivitiesModal 
          title={
            (viewRow.activity_names?.split(",").filter(Boolean).length || 0) === 1 
              ? "Mapped Activity" 
              : "Mapped Activities"
          }
          data={{ 
            ...viewRow, 
            location_names: viewRow.activity_names 
          }} 
          onClose={() => setViewRow(null)} 
        />
      )}

    </div>
  );
};

export default ActivityMappingPage;